import {useCallback, useMemo, useState} from "react";

const NEXT_TYPES = {
	ROOT: ["CONSORZIO"],
	CONSORZIO: ["COOPERATIVA"],
	COOPERATIVA: ["CANALE"],
	CANALE: []
};

function hashStr(s)
{
	let h = 2166136261 >>> 0;
	for (let i = 0; i < s.length; i++)
	{
		h ^= s.charCodeAt(i);
		h = Math.imul(h, 16777619);
	}
	return h >>> 0;
}

function demoLeafMeta(node)
{
	// demo stabile per qualsiasi leaf != ROOT
	const key = node.serverId || node.key || node.name;
	const h = hashStr(String(key));
	const pdv = (h % 6) + 1;                 // 1..7 (più realistico: 1..7)
	const flyers = ((h >>> 3) % 4);          // 0..3
	return {pdv, flyers};
}

export function useOrgTree(initial)
{
	const [nodes, setNodes] = useState(() =>
	{
		if (initial) return initial;

		return [{
			key: "root",
			name: "VolantinoPiù",
			type: "ROOT",
			serverId: null,
			children: []
		}];
	});

	const allowedChildren = useCallback((type) =>
	{
		return NEXT_TYPES[type] || [];
	}, []);

	const findNode = useCallback((list, key, path = []) =>
	{
		for (const n of list)
		{
			const p = [...path, n];
			if (n.key === key) return {node: n, path: p};
			if (n.children?.length)
			{
				const f = findNode(n.children, key, p);
				if (f) return f;
			}
		}
		return null;
	}, []);

	const updateNode = useCallback((list, key, updater) =>
	{
		return list.map(n =>
		{
			if (n.key === key)
				return updater(n);

			if (n.children?.length)
				return {...n, children: updateNode(n.children, key, updater)};

			return n;
		});
	}, []);

	const insertChild = useCallback((list, parentKey, child) =>
	{
		return list.map(n =>
		{
			if (n.key === parentKey)
				return {...n, children: [...(n.children || []), child]};

			if (n.children?.length)
				return {...n, children: insertChild(n.children, parentKey, child)};

			return n;
		});
	}, []);

	const removeNode = useCallback((list, key) =>
	{
		return list.filter(n => n.key !== key).map(n =>
		{
			if (n.children?.length)
				return {...n, children: removeNode(n.children, key)};
			return n;
		});
	}, []);

	// CRUD ------------------------------------------------------------
	const addChild = useCallback((parentKey, name) =>
	{
		const f = findNode(nodes, parentKey);
		if (!f) return null;

		const next = allowedChildren(f.node.type);
		if (!next.length) return null;

		const childType = next[0];
		const child = {
			key: `${childType}-${Date.now()}`,
			name: name.trim(),
			type: childType,
			children: [],
			allowedProductFields: []
		};

		setNodes(list => insertChild(list, parentKey, child));
		return child.key;
	}, [nodes, allowedChildren, findNode, insertChild]);

	const renameNode = useCallback((key, name) =>
	{
		setNodes(list => updateNode(list, key, n => ({...n, name: name.trim()})));
	}, [updateNode]);

	const deleteNode = useCallback((key) =>
	{
		if (key === "root")
			return false;

		setNodes(list => removeNode(list, key));
		return true;
	}, [removeNode]);
	// CRUD ------------------------------------------------------------

	const setFromServer = useCallback((items) =>
	{
		const byId = new Map();
		for (const it of items)
		{
			if (it.type === "ROOT")
				continue;

			const id = String(it._id);
			byId.set(id, {
				key: id,
				name: it.name,
				type: it.type,
				serverId: id,
				parentId: it.parentId ? String(it.parentId) : null,
				allowedProductFields: Array.isArray(it.allowedProductFields) ? it.allowedProductFields : [],
				aiModel: typeof it.aiModel === "string" ? it.aiModel : "",
				children: [],
				meta: {pdv: 0, flyers: 0}
			});
		}

		const rootItem = items.find(x => x.type === "ROOT") || null;
		const rootServerId = rootItem?._id ? String(rootItem._id) : null;

		const root = {
			key: "root",
			name: rootItem?.name || "VolantinoPiù",
			type: "ROOT",
			serverId: rootServerId,
			allowedProductFields: Array.isArray(rootItem?.allowedProductFields) ? rootItem.allowedProductFields : [],
			children: [],
			meta: {pdv: 0, flyers: 0}
		};

		for (const node of byId.values())
		{
			const pid = node.parentId;
			if (rootServerId && pid === rootServerId)
				root.children.push(node);
			else
			{
				const p = pid ? byId.get(pid) : null;
				if (p) p.children.push(node);
				else root.children.push(node);
			}
		}

		// rollup meta (post-order)
		const roll = (n) =>
		{
			if (!n.children?.length)
			{
				if (n.type !== "ROOT")
					n.meta = demoLeafMeta(n);
				return n.meta;
			}

			let pdv = 0, flyers = 0;
			for (const c of n.children)
			{
				const m = roll(c);
				pdv += m.pdv;
				flyers += m.flyers;
			}
			n.meta = {pdv, flyers};
			return n.meta;
		};

		roll(root);
		setNodes([root]);
	}, []);

	// Helpers derivati ------------------------------------------------
	const toAntTree = useCallback((list) =>
	{
		return list.map(n => ({
			key: n.key,
			title: {name: n.name, type: n.type, meta: n.meta},
			children: n.children?.length ? toAntTree(n.children) : []
		}));
	}, []);

	const asTreeData = useMemo(() =>
	{
		return toAntTree(nodes);
	}, [nodes, toAntTree]);

	const setAllowedProductFields = useCallback((key, fields) =>
	{
		if (!key)
			return;

		const sanitized = Array.isArray(fields)
			? fields.filter(Boolean).map(field => ({
				name: typeof field?.name === "string"
					? field.name
					: (typeof field?.key === "string" ? field.key : ""),
				type: typeof field?.type === "string" ? field.type : ""
			})).filter(field => field.name && field.type)
			: [];

		setNodes(list => updateNode(list, key, node => ({
			...node,
			allowedProductFields: sanitized
		})));
	}, [updateNode]);

	const setAiModel = useCallback((key, model) =>
	{
		if (!key)
			return;

		const sanitized = typeof model === "string" ? model.trim() : "";

		setNodes(list => updateNode(list, key, node => ({
			...node,
			aiModel: sanitized
		})));
	}, [updateNode]);

	return {
		nodes,
		asTreeData,
		findNode,
		addChild,
		renameNode,
		deleteNode,
		allowedChildren,
		setFromServer,
		setAllowedProductFields,
		setAiModel
	};
}
